import { useQuery, useQueryClient } from "@tanstack/react-query";
import i18n from "../localization/i18n";
import { fetchCategories, fetchSources, getCollectionsForUser, getRecommendations, getTags, getThinkoCollections } from "./apiService";
import { useUserStore } from "../store/userStore";
import { queryClient } from "./tanStackClient";
const staleTime5 = 5 * 60 * 1000; // 5 minutes
const staleTime60 = 60 * 60 * 1000; // 60 minutes
const staleTime24Hours = 60 * 60 * 1000 * 24; // 24 Hours


export function useCategories() {
  const currentLanguage = i18n.language;
  const { user } = useUserStore.getState();
  return useQuery({
    queryKey: ["categories", currentLanguage, user?.firebaseUser?.uid],
    queryFn: () => fetchCategories(),
    staleTime: staleTime60, // 60 minutes
    refetchOnWindowFocus: false,
  });
}

export function useSources(articleId: string) {
  const currentLanguage = i18n.language;
  const { user } = useUserStore.getState();
  return useQuery({
    queryKey: ["sources", articleId, currentLanguage, user?.firebaseUser?.uid],
    queryFn: () => fetchSources(articleId),
    staleTime: staleTime5, // 5 minutes
    refetchOnWindowFocus: false,
  });
}

export function useTags() {
  const currentLanguage = i18n.language;
  const { user } = useUserStore.getState();
  return useQuery({
    queryKey: ["tags", currentLanguage, user?.firebaseUser?.uid],
    queryFn: () => getTags(),
    staleTime: staleTime5, // 5 minutes
    refetchOnWindowFocus: false,
  });
}

export function useRecommendations(limit: number = 5) {
  const uid = useUserStore(state => state.user?.firebaseUser?.uid);
  return useQuery({
    queryKey: ["recommendations", uid, limit],
    queryFn: () => getRecommendations(limit),
    staleTime: staleTime24Hours, // 24 hours
    refetchOnWindowFocus: false,
  });
}

export function removeCollectionFromCache(collectionId: string) {
  const { user } = useUserStore.getState();
  queryClient.setQueriesData(
    { queryKey: ["recommendations", user?.firebaseUser?.uid] },
    (oldData: any) => {
      if (!oldData) return oldData;
      return oldData.filter( (item: any) => item.id !== collectionId)
    }
  );
  queryClient.setQueriesData(
    { queryKey: ["thinkoCollections"] },
    (oldData: any) => {
      if (!oldData) return oldData;
      return oldData.filter( (item: any) => item.id !== collectionId)
    }
  );
}


export function useThinkoCollections() {
  const uid = useUserStore(state => state.user?.firebaseUser?.uid);
  return useQuery({
    queryKey: ["thinkoCollections", uid],
    queryFn: () => getThinkoCollections(),
    staleTime: staleTime60, // 1 hour
    refetchOnWindowFocus: false,
  });
}

export function useCollections() {
  const uid = useUserStore(state => state.user?.firebaseUser?.uid);
  return useQuery({
    queryKey: ["collections", uid],
    queryFn: () => getCollectionsForUser(),
    staleTime: staleTime60, // 1 hour
    refetchOnWindowFocus: false,
    enabled: !!uid,
  });
}